import {
  applyLocationScope,
  ChartFilters,
  NO_MATCH_LOCATION_ID,
} from "hooks/useAdminGroup/applyLocationScope";
import type { ScopedLocationIds } from "hooks/useAdminGroup/useScopedLocationIds";
import type { HierarchyExternalIds } from "hooks/useAdminGroup/useHierarchyExternalIds";

interface DeriveMergedLocationScopeInput {
  filters: ChartFilters;
  /** The current section's filter options (Redux controls state). */
  filterOptions: unknown;
  scope: ScopedLocationIds;
  hierarchy: HierarchyExternalIds;
}

interface DeriveMergedLocationScopeResult {
  filters: ChartFilters;
  /** Render a spinner instead: a needed input (the hierarchy) is loading. */
  hold: boolean;
  /**
   * Render the empty state instead; no request could return rows (the
   * scope ∩ manual-location-filter intersection is empty).
   */
  isEmptyIntersection: boolean;
}

// Pure decision table for ReportPageChartWithScope (Reporting + Quality
// charts, merge-into-filters.locations design). Wraps applyLocationScope
// with handling for the DEGRADED LOCATION SEED:
//
// The Location views seed `apiFilters.locations` from the /nextos/locations
// options (Mongo OCI cache). That cache can legitimately be empty while the
// breadcrumb hierarchy (AGS/VSOS) still has locations -- the seed is then
// `locations: []`, which is NOT a user narrowing: the location filter UI is
// hidden entirely when there are no options. Left alone, that seed zeroes
// every chart (CDR empty-IN / quality 1=0), and intersecting it with a
// scope sentinels straight to NoData. Recover it from the breadcrumb:
//
//   options [] + node selected -> locations = scope subtree ids (the
//                                 applyLocationScope no-manual branch)
//   options [] + root/default  -> locations = ALL hierarchy node ids (the
//                                 breadcrumb is the source of truth)
//   options [] + root, hierarchy loading -> hold (never fire the [] payload)
//   options [] + root, hierarchy failed/empty -> fail open like the rest of
//                                 LBA (breadcrumb renders disabled): drop
//                                 the empty key, account-wide
//   options [] + LBA off       -> untouched (pre-existing app behavior)
//
// A genuine user remove-all (options exist, selection emptied) keeps
// today's semantics: sentinel/NoData under a scope, passthrough without
// one. Non-Location views never seed a `locations` key, so the degraded
// branch cannot trigger there -- at root they keep the default behavior of
// sending no location filter at all.
//
// Emits ONLY `locations` (never scope_locations): these tabs use the merge
// design, and /v2/nextos/reports/custom/run reads the client's
// filters.locations as its only scope -- the merge is load-bearing
// enforcement there.
export const deriveMergedLocationScope = ({
  filters,
  filterOptions,
  scope,
  hierarchy,
}: DeriveMergedLocationScopeInput): DeriveMergedLocationScopeResult => {
  const locations = filters?.locations;
  const optionsEmpty =
    !Array.isArray(filterOptions) || filterOptions.length === 0;
  const isDegradedSeed =
    Array.isArray(locations) && locations.length === 0 && optionsEmpty;

  let merged: ChartFilters;

  if (isDegradedSeed && scope.isLbaEnabled && filters) {
    const { locations: _dropped, ...rest } = filters;

    if (scope.applyScope) {
      // Treat the seed as "no manual filter": applyLocationScope injects the
      // scope subtree ids (and fails closed to the sentinel on a malformed
      // scope set, which the shared check below turns into the empty state).
      merged = applyLocationScope(rest, scope.applyScope, scope.scopeExternalIds);
    } else if (!hierarchy.isFailed && !hierarchy.isReady) {
      return { filters, hold: true, isEmptyIntersection: false };
    } else if (hierarchy.isFailed || hierarchy.allExternalIds.length === 0) {
      merged = rest;
    } else {
      merged = { ...filters, locations: hierarchy.allExternalIds };
    }
  } else {
    merged = applyLocationScope(filters, scope.applyScope, scope.scopeExternalIds);
  }

  // The scope ∩ manual-location-filter intersection can legitimately be
  // empty (the Location views seed `filters.locations` with the account's
  // location ids, and the user -- or an id-space mismatch -- leaves nothing
  // in common with the breadcrumb scope). The string sentinel keeps such a
  // request safe, but firing it is pointless: every chart would come back
  // empty. Callers short-circuit to the empty state instead.
  const isEmptyIntersection =
    scope.applyScope &&
    Array.isArray(merged?.locations) &&
    merged.locations.length === 1 &&
    merged.locations[0] === NO_MATCH_LOCATION_ID;

  return { filters: merged, hold: false, isEmptyIntersection };
};
